/**
 * Entry point for the admin shell.
 *
 * Two providers and nothing else. Query holds every server value; the router
 * holds where you are. Nothing in this app keeps identity in a store — the
 * session lives in an `httpOnly` cookie and is re-resolved from `/auth/me`,
 * because a client-side copy of "who am I" is a copy that can be wrong at
 * exactly the moment it matters (§2.6 revokes sessions instantly, and a cached
 * identity would not notice).
 */

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError } from './api/client.ts';
import { keys } from './api/queries.ts';
import { applyStoredTheme } from './layout/theme-toggle.tsx';
import { buildRouter } from './router.tsx';
import './styles.css';

/**
 * A session can end while the app is open — §2.3 exists precisely so that
 * revocation takes effect now rather than at a token's expiry, and §2.6 revokes
 * every session the moment someone is deactivated. The router's `beforeLoad`
 * guard only runs on navigation, so without this a person whose session was
 * revoked mid-visit would sit on a screen full of error cards with no route out.
 *
 * A full navigation rather than a router push: every cached query belongs to an
 * identity that no longer holds, and reloading is the only way to be sure none
 * of it survives into the next sign-in.
 *
 * **Only when there was a session to end.** A 401 also arrives on the very
 * first `/auth/me` of a visitor who has simply never signed in, and that is the
 * router guard's case, not this one — it redirects with a `redirect` and no
 * error. Without the cached-session test both handlers fire on that first load,
 * this one wins the race, and someone opening the app for the first time is
 * told their sign-in did not go through when nothing was ever attempted.
 */
function handleEndedSession(error: unknown): void {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  if (window.location.pathname === '/sign-in') return;
  if (queryClient.getQueryData(keys.me) === undefined) return;

  const redirect = `${window.location.pathname}${window.location.search}`;
  const target = new URL('/sign-in', window.location.origin);
  if (redirect !== '/') target.searchParams.set('redirect', redirect);
  target.searchParams.set('error', 'session_ended');
  target.searchParams.set('message', error.message);
  window.location.replace(target.toString());
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleEndedSession }),
  mutationCache: new MutationCache({ onError: handleEndedSession }),
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      /**
       * A 401 means the session ended and a 403 means the answer is no; neither
       * changes by asking again, and retrying a denial would spend the §8.6
       * per-session authorization-failure budget three times for one mistake.
       */
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

const router = buildRouter(queryClient);

// Before first paint, so the app does not flash the light palette on a machine
// set to dark.
applyStoredTheme();

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
